import { useCallback, useEffect, useState } from 'react';
import type { Rol, UsuarioSesion } from '@matias-asset-os/contracts';
import { esIdiomaSoportado, t, type Idioma } from '@matias-asset-os/i18n';
import { api, ErrorApi, usarIdioma } from './api.js';
import { Ajustes } from './pages/Ajustes.js';
import { Calendario } from './pages/Calendario.js';
import { Checklists } from './pages/Checklists.js';
import { Crm } from './pages/Crm.js';
import { Dashboard } from './pages/Dashboard.js';
import { Finanzas } from './pages/Finanzas.js';
import { Ia } from './pages/Ia.js';
import { Importacion } from './pages/Importacion.js';
import { IncidenciaDetalle } from './pages/Incidencia.js';
import { Avisos } from './componentes/Avisos.js';
import { PantallaRota } from './componentes/PantallaRota.js';
import { CapturarArchivos } from './componentes/CapturarArchivos.js';
import { CargaRapida } from './pages/CargaRapida.js';
import { Contacto } from './pages/Contacto.js';
import { Usuarios } from './pages/Usuarios.js';
import { PortalCliente } from './pages/PortalCliente.js';
import { PortalProveedor } from './pages/PortalProveedor.js';
import { Interno } from './pages/Interno.js';
import { Proyectos } from './pages/Proyectos.js';
import { Referidos } from './pages/Referidos.js';
import { FichaPropiedad } from './pages/FichaPropiedad.js';
import { Login } from './pages/Login.js';
import { Notificaciones } from './pages/Notificaciones.js';
import { PanelPropio } from './pages/PanelPropio.js';
import { NuevaPropiedad } from './pages/NuevaPropiedad.js';
import { Partes } from './pages/Partes.js';
import { Propiedades } from './pages/Propiedades.js';
import { Proveedores } from './pages/Proveedores.js';
import { Modal } from './componentes/Modal.js';
import { Icono, type NombreIcono } from './componentes/Iconos.js';

/**
 * El aviso de "datos ficticios" es una advertencia de entorno, no parte del
 * producto. Se muestra en desarrollo, y en un despliegue sólo si se pide con
 * `VITE_DATOS_DEMO=true`.
 */
const MOSTRAR_AVISO_DEMO = import.meta.env.DEV || import.meta.env.VITE_DATOS_DEMO === 'true';

function idiomaInicial(): Idioma {
  const guardado = localStorage.getItem('aos_idioma');
  if (guardado && esIdiomaSoportado(guardado)) return guardado;
  const navegador = navigator.language.slice(0, 2).toLowerCase();
  return esIdiomaSoportado(navegador) ? navegador : 'es';
}

type Tema = 'costa' | 'medianoche' | 'arena';
function temaInicial(): Tema {
  const g = localStorage.getItem('aos_tema');
  return g === 'medianoche' || g === 'arena' ? g : 'costa';
}

interface EntradaMenu {
  clave: ModuloClave;
  icono: NombreIcono;
  texto: string;
  insignia?: number;
}

/*
  Se puede entrar en una ficha por una pestana que no sea el resumen, y en
  Proyectos apuntando a un proyecto. Las dos cosas hacen falta para «Tareas del
  dia»: quien toca una visita quiere hacer algo con ella, y quien toca una
  tarea quiere el proyecto donde vive, no la lista de equipos.
*/
type PestanaFicha = 'resumen' | 'incidencias' | 'visitas' | 'mantenimiento' | 'proveedores' | 'gastos' | 'contratos' | 'historial';

type Vista =
  | { nombre: 'panel' }
  | { nombre: 'registrar' }
  | { nombre: 'portal' }
  | { nombre: 'encargos' }
  | { nombre: 'contacto' }
  | { nombre: 'alertas' }
  | { nombre: 'cartera' }
  | { nombre: 'ficha'; propiedadId: string; pestana?: PestanaFicha }
  | { nombre: 'incidencia'; incidenciaId: string; propiedadId: string }
  | { nombre: 'nueva' }
  | { nombre: 'partes' }
  | { nombre: 'usuarios' }
  | { nombre: 'proveedores' }
  | { nombre: 'calendario' }
  | { nombre: 'checklists' }
  | { nombre: 'interno' }
  | { nombre: 'crm'; oportunidadId?: string }
  | { nombre: 'finanzas' }
  | { nombre: 'proyectos'; proyectoId?: string }
  | { nombre: 'referidos' }
  | { nombre: 'ia' }
  | { nombre: 'ajustes' }
  | { nombre: 'importacion' };

type ModuloClave =
  | 'panel'
  | 'alertas'
  | 'cartera'
  | 'crm'
  | 'finanzas'
  | 'proyectos'
  | 'referidos'
  | 'ia'
  | 'partes'
  | 'usuarios'
  | 'proveedores'
  | 'calendario'
  | 'checklists'
  | 'interno'
  | 'registrar'
  | 'portal'
  | 'encargos'
  | 'contacto'
  | 'ajustes'
  | 'importacion';

function iniciales(nombre: string): string {
  return nombre
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');
}

const TEMAS: Array<[Tema, string, string]> = [
  ['costa', '☀', 'Costa'],
  ['arena', '◐', 'Arena'],
  ['medianoche', '☾', 'Medianoche'],
];

/**
 * LA APLICACION ENTERA VIVE EN UNA SOLA DIRECCION, Y ESO ROMPIA EL BOTON ATRAS.
 *
 * Cada pantalla es un estado de React, no una URL, asi que el navegador nunca
 * apilaba nada: pulsar «atras» estando en una incidencia no volvia a la lista
 * —volvia a lo que hubiera antes de entrar en la aplicacion, normalmente una
 * pestana en blanco—. En movil, donde «atras» es un gesto y no un boton
 * pequeno, eso es salirse de la aplicacion sin querer varias veces al dia.
 *
 * Este envoltorio deja la interfaz igual —`setVista` se usa desde 36 sitios y
 * ninguno cambia— y ademas apila una entrada por pantalla. Asi «atras» hace lo
 * mismo que el boton «Volver» que ya estaba: deshacer el ultimo paso.
 *
 * La vista viaja DENTRO de `history.state`, no en la barra de direcciones. Es
 * deliberado: poner los identificadores en la URL invita a compartirlos y a
 * marcarlos, y una direccion marcada sobrevive a los permisos con los que se
 * creo. Cuando haga falta compartir enlaces sera una decision aparte.
 */
/*
  La vista de partida es una CONSTANTE del modulo, no un literal en la llamada.
  Escrita en la llamada seria un objeto nuevo en cada render, el efecto se
  volveria a ejecutar y su `replaceState` machacaria la entrada actual del
  historial: «atras» dejaria de funcionar justo por el codigo que lo arregla.
*/
const VISTA_INICIAL: Vista = { nombre: 'cartera' };

function useVista(inicial: Vista): [Vista, (v: Vista) => void] {
  const [vista, poner] = useState<Vista>(inicial);

  useEffect(() => {
    // La primera pantalla OCUPA la entrada actual en vez de apilar otra: si
    // apilara, haria falta pulsar «atras» dos veces para salir.
    window.history.replaceState({ vista: inicial }, '');

    const alVolver = (e: PopStateEvent) => {
      const guardada = (e.state as { vista?: Vista } | null)?.vista;
      if (guardada) poner(guardada);
    };
    window.addEventListener('popstate', alVolver);
    return () => window.removeEventListener('popstate', alVolver);
    // Solo al montar: `inicial` es el punto de partida, no una dependencia.
  }, [inicial]);

  const navegar = useCallback((v: Vista) => {
    window.history.pushState({ vista: v }, '');
    poner(v);
  }, []);

  return [vista, navegar];
}

export function App() {
  const [idioma, setIdioma] = useState<Idioma>(idiomaInicial);
  const [tema, setTema] = useState<Tema>(temaInicial);
  const [usuario, setUsuario] = useState<UsuarioSesion | null>(null);
  const [cargando, setCargando] = useState(true);
  const [vista, setVista] = useVista(VISTA_INICIAL);
  const [alertasCount, setAlertasCount] = useState(0);
  const [rolReal, setRolReal] = useState<Rol | null>(null);
  const [viendoComo, setViendoComo] = useState<Rol | null>(null);
  /*
   * La importacion de arranque no es una funcionalidad permanente: es una
   * puerta que se abre una tarde. La entrada del menu solo aparece si el
   * servidor dice que esta habilitada Y que la cartera sigue vacia, de modo que
   * en cuanto los datos entran, la puerta desaparece sola.
   */
  const [puedeImportar, setPuedeImportar] = useState(false);
  /*
   * EN MOVIL NO HAY BARRA LATERAL: la maqueta la esconde por debajo de 60rem.
   * Sin nada que la sustituya, quien entra desde el telefono llega a una
   * pantalla y no puede ir a ninguna otra. La aplicacion se usa de pie en una
   * casa, con el movil en la mano; es el caso principal, no el raro.
   *
   * Abajo van las cuatro entradas que mas se usan y un «Mas» que abre el resto
   * —con el idioma, el tema y el cierre de sesion, que hoy solo viven en el
   * lateral y desde el telefono tampoco se alcanzaban.
   */
  const [menuMovil, setMenuMovil] = useState(false);


  const traducir = useCallback((clave: string) => t(idioma, clave), [idioma]);

  const esInterno = usuario?.rol === 'DIRECTOR' || usuario?.rol === 'MANAGER';
  const moduloActivo: ModuloClave =
    vista.nombre === 'ficha' || vista.nombre === 'nueva' ? 'cartera' : (vista.nombre as ModuloClave);

  /*
    EL IDIOMA SE PONE AL PINTAR, NO DESPUES.

    Estaba dentro del efecto de abajo, y salia al reves: al pulsar EN la lista
    llegaba en castellano y al pulsar ES, en ingles. React ejecuta los efectos
    de los hijos ANTES que los del padre, asi que la pantalla de dentro ya
    habia pedido sus datos —con el idioma anterior todavia puesto— cuando este
    efecto llegaba a cambiarlo. Siempre un paso por detras.

    Pintar, en cambio, va de fuera hacia dentro: aqui se ejecuta antes de que
    ningun hijo exista, y para cuando alguno pida nada, el idioma es el bueno.
    Es una asignacion a una variable, sin efectos ni sorpresas si se repite.
  */
  usarIdioma(idioma);

  useEffect(() => {
    document.documentElement.lang = idioma;
    localStorage.setItem('aos_idioma', idioma);
  }, [idioma]);

  useEffect(() => {
    document.documentElement.dataset.tema = tema;
    localStorage.setItem('aos_tema', tema);
  }, [tema]);

  // Recuento de alertas para la insignia de la barra lateral. Se refresca al
  // cambiar de vista, de modo que resolver una incidencia y salir baja el número.
  useEffect(() => {
    if (!esInterno) {
      setAlertasCount(0);
      return;
    }
    api
      .alertas()
      // Solo las que no se han dado por vistas: una insignia que no se puede
      // apagar deja de mirarse a la semana.
      .then((r) => setAlertasCount(r.alertas.filter((a) => !a.leida).length))
      .catch(() => undefined);
  }, [esInterno, vista]);

  useEffect(() => {
    if (usuario?.rol !== 'DIRECTOR') {
      setPuedeImportar(false);
      return;
    }
    api
      .importacionEstado()
      // Basta con que la puerta este abierta. La instantanea de arranque sigue
      // exigiendo la cartera vacia —lo comprueba el servidor y lo dice la
      // pantalla—, pero los proyectos llegan despues, sobre una empresa que ya
      // tiene datos: con `vacia` aqui, esa entrada no aparecia nunca.
      .then((e) => setPuedeImportar(e.habilitada))
      .catch(() => setPuedeImportar(false));
  }, [usuario, vista]);

  /*
    DONDE ATERRIZA CADA ROL, ESCRITO UNA SOLA VEZ.

    Estaba escrito tres veces —al abrir la app, al cambiar de rol y al entrar—
    y la tercera se habia quedado atras: quien entraba como cliente caia en la
    cartera, que es la pantalla del gestor, y desde ahi en la ficha completa con
    gastos, contratos y proveedores. El servidor negaba los datos, asi que no se
    filtraba nada, pero el cliente veia pestanas que no son suyas en vez de su
    portal. Con una sola funcion no hay una tercera copia que olvidar.
  */
  const inicioDe = (rol: Rol): 'panel' | 'encargos' | 'portal' | 'crm' =>
    rol === 'DIRECTOR' || rol === 'MANAGER'
      ? 'panel'
      : rol === 'CAPTADOR'
        ? // Un captador entra a lo suyo: el CRM es toda su aplicacion.
          'crm'
        : rol === 'PROVEEDOR'
          ? 'encargos'
          : 'portal';

  useEffect(() => {
    api
      .yo()
      .then(({ usuario: u, rolReal: rr, viendoComo: vc }) => {
        setUsuario(u);
        setRolReal(rr);
        setViendoComo(vc);
        setVista({ nombre: inicioDe(u.rol) });
        if (esIdiomaSoportado(u.idioma)) setIdioma(u.idioma);
      })
      .catch((error: unknown) => {
        if (!(error instanceof ErrorApi)) console.error(error);
      })
      .finally(() => setCargando(false));
  }, []);

  const cambiarRol = (rol: Rol | null) => {
    void api
      .verComo(rol)
      .then(() => api.yo())
      .then(({ usuario: u, rolReal: rr, viendoComo: vc }) => {
        setUsuario(u);
        setRolReal(rr);
        setViendoComo(vc);
        setVista({ nombre: inicioDe(u.rol) });
      })
      .catch(() => undefined);
  };

  const alEntrar = (nuevo: UsuarioSesion) => {
    setUsuario(nuevo);
    setRolReal(nuevo.rol);
    setViendoComo(null);
    setVista({ nombre: inicioDe(nuevo.rol) });
    if (esIdiomaSoportado(nuevo.idioma)) setIdioma(nuevo.idioma);
  };

  const salir = async () => {
    await api.salir();
    setUsuario(null);
    setVista({ nombre: 'cartera' });
  };

  const conmutadorIdioma = (
    <div className="conmutador" role="group" aria-label="Idioma">
      {(['es', 'en', 'de'] as const).map((codigo) => (
        <button
          key={codigo}
          type="button"
          aria-pressed={idioma === codigo}
          onClick={() => setIdioma(codigo)}
        >
          {codigo.toUpperCase()}
        </button>
      ))}
    </div>
  );

  // --- Sin sesión: pantalla de acceso --------------------------------------
  if (!usuario) {
    if (cargando) return <p style={{ padding: '2rem' }}>{traducir('app.cargando')}</p>;
    return (
      <Login
        idioma={idioma}
        onEntrar={alEntrar}
        onIdioma={setIdioma}
        avisoDemo={MOSTRAR_AVISO_DEMO}
      />
    );
  }

  // --- Con sesión: marco con barra lateral ---------------------------------
  /*
   * El menu es distinto por rol, y esa diferencia NO es cosmetica.
   *
   * Un manager trabaja sobre las propiedades que tiene asignadas: no lleva la
   * facturacion de la empresa, ni el CRM, ni el directorio de personas. Dejarle
   * esas entradas a la vista solo produce pantallas a las que el servidor le
   * responderia 403; es mas honesto no ofrecerselas. La comprobacion de verdad
   * sigue estando en el servidor: esto solo decide que se ensena.
   */
  /*
    Proyectos NO esta aqui a proposito. El equipo entra y ve LOS SUYOS —de los
    que es miembro o donde tiene tareas—; el servidor es quien lo decide, y sin
    la entrada del menu la mitad del equipo no llegaba a las tareas que la
    portada le pide hacer.
  */
  const SOLO_DIRECCION: ModuloClave[] = [
    'checklists',
    'finanzas',
    'referidos',
    'ia',
    'usuarios',
    'importacion',
  ];

  /*
    El CRM no va por rol, va por permiso: la direccion se lo da y se lo quita a
    cada persona desde «Usuarios». Aqui solo se decide que se ensena; quien
    decide de verdad es el servidor.
  */
  const modulosInternos: EntradaMenu[] = [
        { clave: 'registrar', icono: 'mas', texto: traducir('nav.registrar') },
        {
          clave: 'panel',
          icono: 'panel',
          // El manager ve SU panel, no el de la empresa: el nombre lo dice.
          texto: usuario.rol === 'DIRECTOR' ? 'Panel' : traducir('panel.miPanel'),
        },
        {
          clave: 'alertas',
          icono: 'alerta',
          texto: traducir('alertas.titulo'),
          insignia: alertasCount || undefined,
        },
        { clave: 'cartera', icono: 'casa', texto: traducir('propiedades.titulo') },
        { clave: 'calendario', icono: 'calendario', texto: traducir('calendario.titulo') },
        { clave: 'checklists', icono: 'checklist', texto: traducir('checklists.titulo') },
        { clave: 'crm', icono: 'embudo', texto: 'CRM' },
        { clave: 'finanzas', icono: 'euro', texto: traducir('fin.titulo') },
        { clave: 'interno', icono: 'reloj', texto: traducir('interno.titulo') },
        { clave: 'proyectos', icono: 'tareas', texto: traducir('proy.titulo') },
        { clave: 'referidos', icono: 'compartir', texto: traducir('ref.titulo') },
        { clave: 'ia', icono: 'chispa', texto: traducir('ia.menu') },
        { clave: 'usuarios', icono: 'personas', texto: traducir('equipo.titulo') },
        { clave: 'proveedores', icono: 'llave-inglesa', texto: traducir('proveedores.titulo') },
        { clave: 'ajustes', icono: 'ajustes', texto: traducir('nav.ajustes') },
        ...(puedeImportar
          ? [
              {
                clave: 'importacion' as const,
                icono: 'importar' as const,
                texto: traducir('import.titulo'),
              },
            ]
          : []),
  ];

  const modulosProveedor: EntradaMenu[] = [
    { clave: 'encargos', icono: 'llave-inglesa', texto: traducir('prov.misEncargos') },
    { clave: 'ajustes', icono: 'ajustes', texto: traducir('portal.ajustes') },
  ];

  /*
    UN CAPTADOR SOLO HACE CALLE.

    Su menu tiene dos entradas y ninguna lleva a cartera, a dinero ni a
    clientes. No es un manager con menos permisos: es otro oficio, y ensenarle
    puertas que no puede abrir solo sirve para que las empuje.

    Esto es la mitad de la verja. La otra esta en el servidor, modulo por
    modulo: aqui se decide lo que se VE, alli lo que se PUEDE.
  */
  const modulosCaptador: EntradaMenu[] = [
    { clave: 'crm', icono: 'embudo', texto: 'CRM' },
    { clave: 'ajustes', icono: 'ajustes', texto: traducir('portal.ajustes') },
  ];

  const modulosCliente: EntradaMenu[] = [
    { clave: 'portal', icono: 'casa', texto: traducir('portal.misPropiedades') },
    { clave: 'contacto', icono: 'sobre', texto: traducir('portal.contacto') },
    { clave: 'ajustes', icono: 'ajustes', texto: traducir('portal.ajustes') },
  ];

  /*
    UN PROMOTOR MIRA. Su menu es el del propietario menos «Contacto»: no viene a
    escribirnos, viene a ver como va la casa. Queda su portal y sus ajustes, que
    son su contrasena y sus avisos.
  */
  const modulosPromotor: EntradaMenu[] = [
    { clave: 'portal', icono: 'casa', texto: traducir('portal.misPropiedades') },
    { clave: 'ajustes', icono: 'ajustes', texto: traducir('portal.ajustes') },
  ];

  /*
    LOS CUATRO DE LA BARRA DE ABAJO NO SON «LOS CUATRO PRIMEROS».

    Cogiendo los primeros del lateral, a un director le salian Registrar, Panel,
    Alertas y Propiedades: Registrar es una accion suelta y ocupaba el sitio de
    algo que se visita todos los dias. Aqui se nombran a mano los que se usan a
    diario en cada rol, y el resto vive detras de «Mas», que es de donde se
    sacan una vez a la semana.
  */
  const ABAJO: Record<string, ModuloClave[]> = {
    DIRECTOR: ['panel', 'cartera', 'calendario', 'alertas'],
    MANAGER: ['panel', 'cartera', 'calendario', 'alertas'],
    PROVEEDOR: ['encargos', 'ajustes'],
    CLIENTE: ['portal', 'contacto', 'ajustes'],
    CAPTADOR: ['crm', 'ajustes'],
    PROMOTOR: ['portal', 'ajustes'],
  };

  const modulos: EntradaMenu[] = esInterno
    ? modulosInternos.filter((m) => {
        if (usuario.rol === 'DIRECTOR') return true;
        // El CRM va por permiso, no por rol: se concede desde «Usuarios».
        if (m.clave === 'crm') return usuario.crmHabilitado;
        return !SOLO_DIRECCION.includes(m.clave);
      })
    : usuario.rol === 'PROVEEDOR'
      ? modulosProveedor
      : usuario.rol === 'CAPTADOR'
        ? modulosCaptador
        : usuario.rol === 'PROMOTOR'
          ? modulosPromotor
          : modulosCliente;

  /*
    Se toma la lista de `ABAJO` en su orden y se dejan solo los modulos que el
    rol de verdad tiene. Si el rol no aparece en `ABAJO` —o se queda corto—, se
    completa con los primeros del menu: la barra nunca sale vacia.
  */
  const abajo: EntradaMenu[] = [
    ...(ABAJO[usuario.rol] ?? [])
      .map((clave) => modulos.find((m) => m.clave === clave))
      .filter((m): m is EntradaMenu => m !== undefined),
    ...modulos,
  ]
    .filter((m, i, todos) => todos.findIndex((o) => o.clave === m.clave) === i)
    .slice(0, 4);

  return (
    <div className="marco">
      {/* Fotos, facturas e informes se abren dentro, no en otra pestana. */}
      <CapturarArchivos idioma={idioma} />
      <aside className="lateral">
        <div className="logo">
          {/* La marca de la casa, no unas iniciales: es la misma que ve quien
              se la anade a la pantalla de inicio. */}
          <img className="marca-icono" src="/marca.svg" alt="" width="30" height="30" />
          <span className="marca-texto">
            <b>Soluxe Management</b>
            <small>{usuario.tenantNombre}</small>
          </span>
        </div>

        <nav className="menu" aria-label={traducir('app.modulos')}>
          {modulos.map((m) => (
            <button
              key={m.clave}
              type="button"
              aria-current={moduloActivo === m.clave ? 'page' : undefined}
              onClick={() => setVista({ nombre: m.clave })}
            >
              <Icono nombre={m.icono} />
              <span>{m.texto}</span>
              {m.insignia ? <span className="insignia">{m.insignia}</span> : null}
            </button>
          ))}
        </nav>

        <div className="lateral-pie">
          {/*
            Idioma, tema y campana comparten fila. La campana sola en su propio
            renglon parecia un icono olvidado; con los conmutadores forma una
            barra de utilidades, que es lo que es.
          */}
          <div className="utilidades">
            {conmutadorIdioma}
            <div className="conmutador" role="group" aria-label="Tema">
              {TEMAS.map(([clave, icono, nombre]) => (
                <button
                  key={clave}
                  type="button"
                  aria-pressed={tema === clave}
                  title={nombre}
                  onClick={() => setTema(clave)}
                >
                  {icono}
                </button>
              ))}
            </div>
            {esInterno && (
              <Avisos
                idioma={idioma}
                onAbrir={(a) => {
                  if (a.tipo === 'GASTO_PENDIENTE' || a.tipo === 'PAGO_RECURRENTE') {
                    setVista({ nombre: 'interno' });
                  } else {
                    setVista({ nombre: 'alertas' });
                  }
                }}
              />
            )}
          </div>
          {rolReal === 'DIRECTOR' && (
            <div className="campo ver-como-campo">
              <label htmlFor="ver-como">{traducir('verComo.etiqueta')}</label>
              <select
                id="ver-como"
                value={viendoComo ?? 'DIRECTOR'}
                onChange={(e) =>
                  cambiarRol(e.target.value === 'DIRECTOR' ? null : (e.target.value as Rol))
                }
              >
                <option value="DIRECTOR">{traducir('rol.DIRECTOR')}</option>
                <option value="MANAGER">{traducir('rol.MANAGER')}</option>
                <option value="CLIENTE">{traducir('rol.CLIENTE')}</option>
              </select>
            </div>
          )}
          <div className="ficha-usuario">
            <span className="avatar" aria-hidden="true">
              {iniciales(usuario.nombre)}
            </span>
            <span className="quien">
              <b>{usuario.nombre}</b>
              <small>{traducir(`rol.${usuario.rol}`)}</small>
            </span>
          </div>
          <button type="button" className="boton secundario" onClick={() => void salir()}>
            {traducir('auth.salir')}
          </button>
        </div>
      </aside>

      <div className="contenido">
        {viendoComo && (
          <div className="barra-ver-como" role="status">
            <span>
              {t(idioma, 'verComo.aviso', { rol: t(idioma, `rol.${viendoComo}`) })}
            </span>
            <button type="button" className="enlace" onClick={() => cambiarRol(null)}>
              {t(idioma, 'verComo.volver', { rol: t(idioma, `rol.${rolReal ?? 'DIRECTOR'}`) })}
            </button>
          </div>
        )}
        {MOSTRAR_AVISO_DEMO && (
          <p className="aviso-demo">
            Entorno de desarrollo con <strong>datos ficticios</strong>. Ninguna información real,
            ninguna integración externa conectada.
          </p>
        )}

        {/*
          DOS PANELES, NO UNO CON TROZOS ESCONDIDOS. El de Direccion lleva las
          cuentas de la empresa; el del equipo, lo de cada uno y lo de sus casas.
          Esconder mitad de un panel con condiciones deja un componente que hay
          que leer entero para saber que ve cada persona.
        */}
        {/*
          Cada pantalla, dentro de su red. Si una falla al dibujar, se ve el
          fallo ahi y el menu sigue funcionando; antes se iba la aplicacion
          entera a blanco y no quedaba ni que contar.
        */}
        <PantallaRota
          key={vista.nombre}
          idioma={idioma}
          onVolver={() => setVista({ nombre: 'panel' })}
        >
        {vista.nombre === 'panel' && usuario.rol !== 'DIRECTOR' ? (
          <PanelPropio
            usuario={usuario}
            idioma={idioma}
            onAbrirIncidencia={(incidenciaId, propiedadId) =>
              setVista({ nombre: 'incidencia', incidenciaId, propiedadId })
            }
            onAbrirOportunidad={(oportunidadId) => setVista({ nombre: 'crm', oportunidadId })}
            onAbrirProyecto={(proyectoId) => setVista({ nombre: 'proyectos', proyectoId })}
            onAbrirVisita={(id) =>
              setVista({ nombre: 'ficha', propiedadId: id, pestana: 'visitas' })
            }
          />
        ) : vista.nombre === 'panel' ? (
          <Dashboard
            usuario={usuario}
            idioma={idioma}
            onAbrirPropiedad={(id) => setVista({ nombre: 'ficha', propiedadId: id })}
            onAbrirIncidencia={(incidenciaId, propiedadId) =>
              setVista({ nombre: 'incidencia', incidenciaId, propiedadId })
            }
            onAbrirOportunidad={(oportunidadId) => setVista({ nombre: 'crm', oportunidadId })}
            onAbrirProyecto={(proyectoId) => setVista({ nombre: 'proyectos', proyectoId })}
            onAbrirVisita={(id) =>
              setVista({ nombre: 'ficha', propiedadId: id, pestana: 'visitas' })
            }
          />
        ) : vista.nombre === 'alertas' ? (
          <Notificaciones
            idioma={idioma}
            onVolver={() => setVista({ nombre: 'panel' })}
            onAbrirPropiedad={(id) => setVista({ nombre: 'ficha', propiedadId: id })}
            onLeidas={() => setAlertasCount(0)}
          />
        ) : vista.nombre === 'incidencia' ? (
          <IncidenciaDetalle
            incidenciaId={vista.incidenciaId}
            propiedadId={vista.propiedadId}
            usuario={usuario}
            idioma={idioma}
            onVolver={() => setVista({ nombre: 'ficha', propiedadId: vista.propiedadId })}
          />
        ) : vista.nombre === 'ficha' ? (
          <FichaPropiedad
            propiedadId={vista.propiedadId}
            usuario={usuario}
            idioma={idioma}
            {...(vista.pestana ? { pestanaInicial: vista.pestana } : {})}
            onVolver={() => setVista({ nombre: 'cartera' })}
            onAbrirIncidencia={(incidenciaId) =>
              setVista({ nombre: 'incidencia', incidenciaId, propiedadId: vista.propiedadId })
            }
          />
        ) : vista.nombre === 'nueva' ? (
          <NuevaPropiedad
            idioma={idioma}
            onCreada={(id) => setVista({ nombre: 'ficha', propiedadId: id })}
            onCancelar={() => setVista({ nombre: 'cartera' })}
          />
        ) : vista.nombre === 'partes' ? (
          <Partes idioma={idioma} onVolver={() => setVista({ nombre: 'cartera' })} />
        ) : vista.nombre === 'proveedores' ? (
          <Proveedores idioma={idioma} onVolver={() => setVista({ nombre: 'cartera' })} />
        ) : vista.nombre === 'calendario' ? (
          <Calendario
            idioma={idioma}
            onVolver={() => setVista({ nombre: 'cartera' })}
            onAbrirPropiedad={(id) => setVista({ nombre: 'ficha', propiedadId: id })}
          />
        ) : vista.nombre === 'checklists' ? (
          <Checklists idioma={idioma} onVolver={() => setVista({ nombre: 'cartera' })} />
        ) : vista.nombre === 'usuarios' ? (
          <Usuarios idioma={idioma} yoId={usuario.id} />
        ) : vista.nombre === 'contacto' ? (
          <Contacto idioma={idioma} />
        ) : vista.nombre === 'encargos' ? (
          <PortalProveedor idioma={idioma} nombre={usuario.nombre.split(/\s+/)[0] ?? ''} />
        ) : vista.nombre === 'portal' ? (
          <PortalCliente
            idioma={idioma}
            nombre={usuario.nombre.split(/\s+/)[0] ?? ''}
            soloMirar={usuario.rol === 'PROMOTOR'}
          />
        ) : vista.nombre === 'registrar' ? (
          <CargaRapida
            idioma={idioma}
            rol={usuario.rol}
            onHecho={() => undefined}
            onSalir={() => setVista({ nombre: 'interno' })}
          />
        ) : vista.nombre === 'interno' ? (
          <Interno
            idioma={idioma}
            esDireccion={usuario.rol === 'DIRECTOR'}
            onVolver={() => setVista({ nombre: 'panel' })}
          />
        ) : vista.nombre === 'crm' ? (
          <Crm
            idioma={idioma}
            rol={usuario.rol}
            nombreUsuario={usuario.nombre}
            onVolver={() => setVista({ nombre: 'cartera' })}
            {...(vista.oportunidadId ? { abrirOportunidadId: vista.oportunidadId } : {})}
          />
        ) : vista.nombre === 'finanzas' ? (
          <Finanzas usuario={usuario} idioma={idioma} onVolver={() => setVista({ nombre: 'cartera' })} />
        ) : vista.nombre === 'proyectos' ? (
          <Proyectos
            idioma={idioma}
            esDireccion={usuario.rol === 'DIRECTOR'}
            proyectoInicial={vista.proyectoId}
            onVolver={() => setVista({ nombre: 'cartera' })}
          />
        ) : vista.nombre === 'referidos' ? (
          <Referidos usuario={usuario} idioma={idioma} onVolver={() => setVista({ nombre: 'cartera' })} />
        ) : vista.nombre === 'ia' ? (
          <Ia idioma={idioma} onVolver={() => setVista({ nombre: 'cartera' })} />
        ) : vista.nombre === 'importacion' ? (
          <Importacion idioma={idioma} onVolver={() => setVista({ nombre: 'panel' })} />
        ) : vista.nombre === 'ajustes' ? (
          <Ajustes usuario={usuario} idioma={idioma} onVolver={() => setVista({ nombre: 'cartera' })} />
        ) : (
          <Propiedades
            usuario={usuario}
            idioma={idioma}
            onAbrir={(id) => setVista({ nombre: 'ficha', propiedadId: id })}
            onNueva={() => setVista({ nombre: 'nueva' })}
            onPartes={() => setVista({ nombre: 'partes' })}
          />
        )}
        </PantallaRota>
      </div>

      {/* Navegacion inferior. Solo se ve en movil; la hoja de estilo la esconde
          en cuanto hay sitio para el lateral. */}
      <nav className="barra-inferior" aria-label={traducir('app.navegacion')}>
        {abajo.map((m) => (
          <button
            key={m.clave}
            type="button"
            aria-current={vista.nombre === m.clave ? 'page' : undefined}
            onClick={() => {
              setMenuMovil(false);
              setVista({ nombre: m.clave } as Vista);
            }}
          >
            <Icono nombre={m.icono} />
            <span>{m.texto}</span>
            {m.insignia ? <span className="insignia">{m.insignia}</span> : null}
          </button>
        ))}
        {/*
          «Mas» se enciende cuando lo que hay abierto no tiene boton propio
          abajo. Si no, estando en Finanzas la barra entera se ve apagada y
          parece que no se esta en ningun sitio.
        */}
        <button
          type="button"
          aria-expanded={menuMovil}
          aria-current={
            !menuMovil && !abajo.some((m) => m.clave === vista.nombre) ? 'page' : undefined
          }
          onClick={() => setMenuMovil((v) => !v)}
        >
          <Icono nombre="menu" />
          <span>{traducir('nav.mas')}</span>
        </button>
      </nav>

      {menuMovil && (
        <Modal
          idioma={idioma}
          titulo={traducir('nav.mas')}
          variante="hoja"
          onCerrar={() => setMenuMovil(false)}
        >
          <nav className="menu-movil" aria-label={traducir('app.modulos')}>
            {modulos.map((m) => (
              <button
                key={m.clave}
                type="button"
                aria-current={vista.nombre === m.clave ? 'page' : undefined}
                onClick={() => {
                  setMenuMovil(false);
                  setVista({ nombre: m.clave } as Vista);
                }}
              >
                <Icono nombre={m.icono} />
                <span>{m.texto}</span>
                {m.insignia ? <span className="insignia">{m.insignia}</span> : null}
              </button>
            ))}
          </nav>

          {/* Lo que tambien vivia solo en el lateral. */}
          <div className="utilidades">
            {conmutadorIdioma}
            <div className="conmutador" role="group" aria-label="Tema">
              {TEMAS.map(([clave, icono, nombre]) => (
                <button
                  key={clave}
                  type="button"
                  aria-pressed={tema === clave}
                  title={nombre}
                  onClick={() => setTema(clave)}
                >
                  {icono}
                </button>
              ))}
            </div>
          </div>

          <div className="ficha-usuario">
            <span className="avatar" aria-hidden="true">
              {iniciales(usuario.nombre)}
            </span>
            <span className="quien">
              <b>{usuario.nombre}</b>
              <small>{traducir(`rol.${usuario.rol}`)}</small>
            </span>
          </div>

          <button
            type="button"
            className="secundario salir"
            onClick={() => {
              setMenuMovil(false);
              void salir();
            }}
          >
            {traducir('auth.salir')}
          </button>
        </Modal>
      )}
    </div>
  );
}
